import { KEYCODE } from '@/constants';

/**
 * Class definition for `wp-nav` element
 *
 * @class WPNav
 * @extends {HTMLElement}
 */
class WPNav extends HTMLElement {
  constructor(self) {
    self = super(self);
    this._overlay = document.querySelector('[data-overlay]');
    return self;
  }

  /**
   * Tag name of element
   *
   * @readonly
   * @static
   */
  static get is() {
    return 'wp-nav';
  }

  /**
   * Watched attributes
   *
   * @readonly
   * @static
   */
  static get observedAttributes() {
    return ['opened'];
  }

  /**
   * Getter for `opened` attribute
   */
  get opened() {
    return this.hasAttribute('opened');
  }

  /**
   * Setter for `opened` attribute
   */
  set opened(value) {
    if (Boolean(value)) {
      this.setAttribute('opened', '');
    } else {
      this.removeAttribute('opened');
    }
  }

  get _menuItems() {
    return [...this.querySelectorAll('[data-nav-item]')];
  }

  connectedCallback() {
    this.addEventListener(
      'menuitemchanged',
      this._handleMenuItemChanged,
      false,
    );
    this.addEventListener('subnavclosed', this._handleSubNavClosed, false);
    this.addEventListener('keydown', this._handleKeyDown, false);
    this._overlay.addEventListener('click', this._close.bind(this), false);
  }

  /**
   * Lifecycle event when watched attribute is changed
   *
   * @param {string} name Attribute name
   * @param {any} oldVal Old value of attribute
   * @param {any} newVal New value of attribute
   */
  attributeChangedCallback(name, oldVal, newVal) {
    switch (name) {
      case 'opened':
        this._openedChanged();
    }
  }

  /**
   * Called when `opened` attribute changed
   *
   * @private
   */
  _openedChanged() {
    // If opened, scroll to top
    if (this.opened) {
      this.scrollTop = 0;
      document.body.classList.add('nav-is-open');
    } else {
      document.body.classList.remove('nav-is-open');
      this._close();
    }
  }

  /**
   * Event handler for `keydown` event
   *
   * @private
   * @param {UIEvent} evt
   */
  _handleKeyDown(evt) {
    const keyCode = evt.which;

    // If menu open on ESC, close
    if (
      keyCode === KEYCODE.ESC &&
      this._menuItems.find((menuItem) => menuItem.expanded)
    ) {
      this._menuItems.find((menuItem) => menuItem.expanded).expanded = false;
    }
  }

  _close() {
    if (this._menuItems.find((menuItem) => menuItem.expanded)) {
      this._menuItems.find((menuItem) => menuItem.expanded).expanded = false;
    }
  }

  _handleMenuItemChanged(evt) {
    const currentMenuItem = evt.detail;

    // If currentmenu item is not the expanded one
    if (currentMenuItem.expanded) {
      this._expandedCurrentMenuItem = currentMenuItem;

      // Close all other menu's that are not this menuitem and are expanded
      const expanded = this._menuItems.filter((menuitem) => {
        return menuitem.expanded && menuitem !== currentMenuItem;
      });

      expanded.map((el) => (el.expanded = false));
    }

    // If any are still left expanded, open overlay, else close
    if (this._menuItems.find((el) => el.expanded)) {
      this._openOverlay();

      // Short delay allowing time for subnav to transition in then
      // we disable the transition so the other dropdowns can be visible
      // without fading in and down
      setTimeout(() => this._disableSubNavTransition(), 330);
    } else {
      this._closeOverlay();
      this._enableSubNavTransition();
      this._expandedCurrentMenuItem = null;
    }
  }

  _openOverlay() {
    document.body.classList.add('sub-nav-is-open');
  }

  _closeOverlay() {
    document.body.classList.remove('sub-nav-is-open');
  }

  _disableSubNavTransition() {
    document.body.classList.add('sub-nav-transition-disabled');
  }

  _enableSubNavTransition() {
    document.body.classList.remove('sub-nav-transition-disabled');
  }

  /**
   * Handle closing of subnav @ mobile
   *
   * @param {UIEvent} evt
   */
  _handleSubNavClosed(evt) {
    const navItem =
      evt.target.parentNode.parentNode.parentNode.querySelector('wp-nav-item');

    if (!navItem) return;

    navItem.expanded = false;
  }
}

customElements.define(WPNav.is, WPNav);
